import { useForm } from 'react-hook-form';
import { useHandleSubmitTunnel } from '../../../../hooks/useHandleSubmitTunnel';
import { BInput, BInputError, BRadio } from '../../../../components/ui/form';
import { useEffect, useState } from 'react';
import { IconAt, IconCalendarFilled, IconInfoCircleFilled, IconPhoneFilled, IconUserFilled } from '@tabler/icons-react';
import { BAlert, BButton, BCard } from '../../../../components/ui/generic';
import { differenceInYears, format, subYears } from 'date-fns';
import { useParams } from 'react-router';
import { useAgeQualification } from '../../../../hooks/Settings';
import { useTrainingInfoById } from '../../../../hooks/Training';
import { requiredField } from '../../../../utils';
import { Tooltip } from 'react-tooltip';
import BSelect from '../../../../components/ui/form/BSelect';
import { euNationalityOptions } from '../../../../components/ui/generic/utils';

const Informations = () => {
  const { trainingId } = useParams();
  const { data, isSuccess } = useTrainingInfoById(trainingId);
  const [canEditBirthday, setCanEditBirthday] = useState(false);
  const { data: ageQualification, isSuccess: ageQualification_isSuccess } = useAgeQualification();
  const [dateGreaterThan29, setDateGreaterThan29] = useState(false);
  const {
    watch,
    setValue,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();
  const onSubmit = useHandleSubmitTunnel('infosClient', trainingId);
  const watchBirthday = watch('birthday');
  const watchAgeQualification = watch('age_qualification');
  const watchNationality = watch('nationalityCheckbox');
  useEffect(() => {
    if (!watchBirthday) return;
    const date = new Date(watchBirthday);
    const yearsDifference = differenceInYears(new Date(), date);
    setDateGreaterThan29(yearsDifference > 29);
  }, [watchBirthday]);
  useEffect(() => {
    if (isSuccess) {
      if (data?.user?.birth_day) {
        setValue('birthday', format(data?.user.birth_day, 'yyyy-MM-dd'));
        setCanEditBirthday(false);
      } else setCanEditBirthday(true);
      setValue('gender', data?.user.gender);
      setValue('name', data?.user.name);
      setValue('surname', data?.user.surname);
      setValue('email', data?.user.email);
      setValue('phone', data?.user.phone);
      if (!data.user.nationality) setValue('nationality', data.user.nationality);
      else {
        setValue('nationality', data.user.nationality);
        setValue('nationalityCheckbox', data.user.nationality === 'Française' ? 'fr' : 'eu');
      }
      if (data?.user?.user_address?.main) {
        const main = data?.user?.user_address?.main;
        setValue('city', main.city);
        setValue('postcode', main.postcode);
        setValue('country', data?.user.country ?? 'FRANCE');
        setValue('street', main.street ?? '');
        setValue('number', main.number ?? '');
      }
      if (data?.user?.user_qualification?.age_qualification)
        setValue('age_qualification', data?.user?.user_qualification?.age_qualification + '');
    }
  }, [isSuccess, setValue, data]);
  useEffect(() => {
    if (watchNationality === 'fr') setValue('nationality', 'Française');
  }, [setValue, watchNationality]);
  const today = new Date();
  const maxDate = format(today, 'yyyy-MM-dd');
  const minDate = format(subYears(today, 100), 'yyyy-MM-dd');
  return (
    <BCard maxSize={'md:max-w-2xl'}>
      <BCard.Header title={'Étape 1 : Informations'} withTitle />
      <BCard.Body>
        <form
          id={'tunnelFormStep0'}
          onSubmit={handleSubmit(onSubmit)}
          className={'flex flex-col gap-6 px-4 sm:max-w-md'}
        >
          <input {...register('forcePhone')} type={'hidden'} defaultValue={0} />
          <input {...register('submit')} type={'hidden'} defaultValue={'next'} />
          <div className={'flex flex-col gap-1'}>
            <p className={'text-sm font-normal text-slate-700'}>Civilité</p>
            <div className={'flex gap-2'}>
              <BRadio
                value={'Homme'}
                label={'Homme'}
                id={'genderH'}
                {...register('gender', { required: { value: true, message: requiredField } })}
              />
              <BRadio value={'Femme'} label={'Femme'} id={'genderF'} {...register('gender')} />
            </div>
            <BInputError errors={errors.gender} />
          </div>
          <BInput
            label={'Nom'}
            {...register('name', { required: { value: true, message: requiredField } })}
            icon={IconUserFilled}
            errors={errors?.name}
          />
          <BInput
            label={'Prénom'}
            {...register('surname', { required: { value: true, message: requiredField } })}
            icon={IconUserFilled}
            errors={errors?.surname}
          />
          <div className={'flex flex-col gap-1'}>
            <div className={'flex items-center gap-2'}>
              <Tooltip id={'nationality_helper'} />
              <p className={'text-sm font-normal text-slate-700'}>De quelle nationalité êtes-vous ?</p>
              <IconInfoCircleFilled
                data-tooltip-content={
                  'Cette information est nécessaire pour vérifier votre éligibilité au dispositif d’alternance.'
                }
                data-tooltip-id={'nationality_helper'}
                className="text-primary-800 me-2 h-4 w-4 shrink-0"
              />
            </div>
            <div className={'flex gap-2'}>
              <BRadio
                value={'fr'}
                label={'Française'}
                id={'nationalityFR'}
                {...register('nationalityCheckbox', { required: { value: true, message: requiredField } })}
              />
              <BRadio value={'eu'} label={'Européenne'} id={'nationalityEU'} {...register('nationalityCheckbox')} />
              <BRadio value={'other'} label={'Autre'} id={'nationalityOther'} {...register('nationalityCheckbox')} />
              <BInputError errors={errors.nationality} />
            </div>

            {watchNationality === 'eu' && (
              <BSelect
                options={euNationalityOptions}
                {...register('nationality', {
                  required: { value: true, message: requiredField },
                })}
              />
            )}
            <BAlert color={'warning'} hidden={watchNationality !== 'other'}>
              Nous vous invitons à contacter un conseiller d’admission au{' '}
              <a className={'underline'} href={'tel:+33176497173'}>
                01 76 49 71 73
              </a>{' '}
              afin d’étudier d’autres possibilités de financement
            </BAlert>
          </div>

          <BInput
            {...register('birthday', {
              required: { value: true, message: requiredField },
            })}
            disabled={!canEditBirthday}
            label="Date de naissance"
            type="date"
            icon={IconCalendarFilled}
            errors={errors?.birthday}
            min={minDate}
            max={maxDate}
          />
          <BAlert title={'Vous avez plus de 29 ans ?'} hidden={!dateGreaterThan29}>
            <p className={'text-xs font-normal text-slate-700'}>
              Si oui, veuillez sélectionner celle qui vous correspond:
            </p>
            <div className={'space-y-4 p-4'}>
              {ageQualification_isSuccess &&
                ageQualification.map((age, index) => {
                  return (
                    <div key={index} className="flex items-center gap-2">
                      <BRadio value={age.id} label={age.name} id={'qual' + age.id} {...register('age_qualification')} />
                    </div>
                  );
                })}
            </div>
            <BAlert color={'error'} hidden={watchAgeQualification !== '5'}>
              Vous ne remplissez pas les conditions pour bénéficier du dispositif de l’alternance. Toutefois, d’autres
              solutions de financement peuvent vous être proposées. Nous vous invitons à contacter un conseiller
              d’admission au{' '}
              <a className={'underline'} href={'tel:+33176497173'}>
                01 76 49 71 73
              </a>{' '}
              afin d’étudier ensemble les options adaptées à votre situation.
            </BAlert>
          </BAlert>

          <BInput
            errors={errors.phone}
            label={'Téléphone'}
            disabled
            {...register('phone', {
              required: { value: true, message: requiredField },
              setValueAs: (v) => v.replace(/\s+/g, ''), // remove spaces
              pattern: {
                value: /^\+\d{1,3}\d{9}$/,
                message: 'Format attendu: +XXX suivi de 9 chiffres',
              },
            })}
            icon={IconPhoneFilled}
          />

          <BInput
            disabled
            label={'Email'}
            {...register('email', { required: { value: true, message: requiredField } })}
            icon={IconAt}
          />
          <BAlert>

            <p>
              Pour toute modification de votre adresse email ou de votre numéro de téléphone, merci de contacter
              directement votre conseiller d’admission au{' '}
              <a href={'tel:+33171182397'} className={'underline'}>
                01 71 18 23 97
              </a>
              .
            </p>
          </BAlert>
          <div className={'flex w-full max-w-sm flex-col gap-2 text-sm font-medium text-slate-700'}>
            <div className={'flex flex-col gap-2'}>
              <div className={'flex gap-4'}>
                <div>
                  <BInput
                    errors={errors?.postcode}
                    {...register('postcode', { required: { value: true, message: requiredField } })}
                    label={'Code postal'}
                  />
                </div>
                <BInput
                  errors={errors?.city}
                  {...register('city', { required: { value: true, message: requiredField } })}
                  label={'Ville'}
                />
              </div>
              <div className={'flex gap-4'}>
                <div>
                  <BInput
                    errors={errors?.number}
                    {...register('number', { required: { value: true, message: requiredField } })}
                    label={'N°'}
                  />
                </div>
                <BInput
                  {...register('street', { required: { value: true, message: requiredField } })}
                  label={'Libellé'}
                  errors={errors?.street}
                />
              </div>
              <BInput
                errors={errors?.country}
                {...register('country', { required: { value: true, message: requiredField } })}
                label={'Pays'}
              />
            </div>
          </div>
        </form>
        <div className={'flex justify-end'}>
          <div>
            <BButton
              isProcessing={isSubmitting}
              isDisabled={isSubmitting || watchNationality === 'other' || watchAgeQualification === '5'}
              form={`tunnelFormStep0`}
              size={'base'}
              type={'submit'}
              label={'Suivant'}
            />
          </div>
        </div>
      </BCard.Body>
    </BCard>
  );
};

export default Informations;
